/**
 * Session-scoped recovery for assistant text that has streamed but may not
 * have reached backend persistence yet. Key by message for multi-model turns.
 */
import { pickAuthoritativeMessageText } from '@/constants/utils/message';

export const STREAMING_DRAFT_STORAGE_KEY = 'gab-streaming-drafts';
export const STREAMING_DRAFT_MAX_AGE_MS = 2 * 60 * 60 * 1000;
export const STREAMING_DRAFT_MAX_CHARS = 200000;
const WRITE_MIN_INTERVAL_MS = 2000;
const WRITE_MIN_GROWTH_CHARS = 2000;
const lastWrites = new Map();

function getSessionStorage() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch (_) {
    return null;
  }
}

function readAllDrafts() {
  const storage = getSessionStorage();
  if (!storage) return {};
  try {
    const parsed = JSON.parse(storage.getItem(STREAMING_DRAFT_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeAllDrafts(map) {
  const storage = getSessionStorage();
  if (!storage) return false;
  try {
    storage.setItem(STREAMING_DRAFT_STORAGE_KEY, JSON.stringify(map));
    return true;
  } catch (_) {
    return false;
  }
}

function getConversationDrafts(all, conversationId) {
  const stored = all?.[conversationId];
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
  // Migrate the original single-draft shape lazily.
  return stored.messageId ? { [stored.messageId]: stored } : stored;
}

export function writeStreamingDraft({
  conversationId,
  messageId,
  parentMessageId,
  content,
  thinkingContent,
  force = false,
} = {}) {
  if (!conversationId || !messageId || typeof content !== 'string' || !content.trim()) {
    return false;
  }
  const now = Date.now();
  const key = `${conversationId}:${messageId}`;
  const lastWrite = lastWrites.get(key);
  if (
    !force
    && lastWrite
    && now - lastWrite.at < WRITE_MIN_INTERVAL_MS
    && content.length - lastWrite.len < WRITE_MIN_GROWTH_CHARS
  ) {
    return false;
  }

  const clipped = content.slice(0, STREAMING_DRAFT_MAX_CHARS);
  const all = readAllDrafts();
  const drafts = getConversationDrafts(all, conversationId);
  drafts[messageId] = {
    messageId,
    parentMessageId: parentMessageId || null,
    content: clipped,
    thinkingContent: typeof thinkingContent === 'string'
      ? thinkingContent.slice(0, STREAMING_DRAFT_MAX_CHARS)
      : '',
    updatedAt: now,
  };
  all[conversationId] = drafts;
  if (!writeAllDrafts(all)) return false;
  lastWrites.set(key, { at: now, len: clipped.length });
  return true;
}

export function readStreamingDrafts(conversationId) {
  if (!conversationId) return {};
  const all = readAllDrafts();
  const stored = getConversationDrafts(all, conversationId);
  const valid = {};
  let changed = false;
  Object.entries(stored).forEach(([messageId, draft]) => {
    const isValid =
      draft
      && typeof draft.content === 'string'
      && draft.content.trim()
      && draft.updatedAt
      && Date.now() - draft.updatedAt <= STREAMING_DRAFT_MAX_AGE_MS;
    if (isValid) valid[messageId] = draft;
    else changed = true;
  });
  if (changed) {
    if (Object.keys(valid).length) all[conversationId] = valid;
    else delete all[conversationId];
    writeAllDrafts(all);
  }
  return valid;
}

export function readStreamingDraft(conversationId, messageId = null) {
  const drafts = readStreamingDrafts(conversationId);
  if (messageId) return drafts[messageId] || null;
  return Object.values(drafts)
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))[0] || null;
}

export function clearStreamingDraft(conversationId, messageId = null) {
  if (!conversationId) return;
  const all = readAllDrafts();
  const drafts = getConversationDrafts(all, conversationId);
  if (!Object.keys(drafts).length) return;
  if (messageId) {
    delete drafts[messageId];
    if (Object.keys(drafts).length) all[conversationId] = drafts;
    else delete all[conversationId];
    lastWrites.delete(`${conversationId}:${messageId}`);
  } else {
    delete all[conversationId];
    [...lastWrites.keys()]
      .filter(key => key.startsWith(`${conversationId}:`))
      .forEach(key => lastWrites.delete(key));
  }
  writeAllDrafts(all);
}

export function clearStreamingDraftsForParent(conversationId, parentMessageId) {
  if (!conversationId || !parentMessageId) return;
  Object.values(readStreamingDrafts(conversationId))
    .filter(draft => String(draft.parentMessageId) === String(parentMessageId))
    .forEach(draft => clearStreamingDraft(conversationId, draft.messageId));
}

export function mergeStreamingDraftMessages(conversationId, messages) {
  const drafts = Object.values(readStreamingDrafts(conversationId))
    .sort((a, b) => (a.updatedAt || 0) - (b.updatedAt || 0));
  if (!drafts.length) return messages || [];

  let list = Array.isArray(messages) ? messages : [];
  const lastIncoming = list[list.length - 1];
  const lastIncomingId = lastIncoming?.id || lastIncoming?._id;
  drafts.forEach((draft) => {
    const existing = list.find(m => m?.id === draft.messageId || m?._id === draft.messageId);
    if (existing) {
      const mergedContent = pickAuthoritativeMessageText(draft.content, existing.content);
      if (mergedContent === existing.content) {
        if ((existing.content || '').length >= draft.content.length) {
          clearStreamingDraft(conversationId, draft.messageId);
        }
        return;
      }
      list = list.map(m => (
        m?.id === draft.messageId || m?._id === draft.messageId
          ? {
              ...m,
              content: mergedContent,
              thinkingContent: pickAuthoritativeMessageText(draft.thinkingContent, m.thinkingContent),
              isTruncated: true,
              stopReason: m.stopReason || 'length',
            }
          : m
      ));
      return;
    }
    if (
      lastIncoming?.role !== 'user'
      || !draft.parentMessageId
      || String(lastIncomingId) !== String(draft.parentMessageId)
    ) {
      return;
    }
    list = [...list, {
      id: draft.messageId,
      role: 'assistant',
      content: draft.content,
      thinkingContent: draft.thinkingContent || '',
      isTruncated: true,
      stopReason: 'length',
      timestamp: draft.updatedAt,
      isStreaming: false,
      isThinking: false,
      isWaiting: false,
      isTyping: false,
    }];
  });
  return list;
}

export function flushOpenStreamingDrafts(chats) {
  if (!Array.isArray(chats)) return;
  chats.forEach((chat) => {
    if (!chat?.id || !(chat.isInferencing || chat.isInferencingWaiting)) return;
    (chat.messages || [])
      .filter(m =>
        m?.role === 'assistant'
        && (m.isStreaming || m.isTyping || m.isWaiting || m.isThinking)
        && typeof m.content === 'string'
        && m.content.trim()
      )
      .forEach(streaming => writeStreamingDraft({
        conversationId: chat.id,
        messageId: streaming.id,
        parentMessageId: streaming.parentMessageId,
        content: streaming.content,
        thinkingContent: streaming.thinkingContent,
        force: true,
      }));
  });
}
